import React, { useState, useEffect } from 'react';
import { http } from '../../lib/http';

const DriverManagementTab = ({ setMessage, setError }) => {
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [availableDrivers, setAvailableDrivers] = useState([]);
    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [loadingDrivers, setLoadingDrivers] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newRouteName, setNewRouteName] = useState('');
    const [creatingRoute, setCreatingRoute] = useState(false);

    useEffect(() => {
        loadRoutes();
    }, []);

    const loadRoutes = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await http.get('/admin/driver-management/routes', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setRoutes(response.data.routes || []);
            }
        } catch (error) {
            console.error('Error loading routes:', error);
            setError('Failed to load routes');
        } finally {
            setLoading(false);
        }
    };

    // Load unassigned drivers (driver users not currently assigned to any route)
    const loadAvailableDrivers = async () => {
        setLoadingDrivers(true);
        try {
            const token = localStorage.getItem('token');
            const response = await http.get('/admin/driver-management/drivers', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                // Get all driver IDs currently assigned to routes
                const assignedDriverIds = new Set();
                routes.forEach(route => {
                    if (route.driver_ids && Array.isArray(route.driver_ids)) {
                        route.driver_ids.forEach(id => assignedDriverIds.add(id));
                    }
                    if (route.driver_id) {
                        assignedDriverIds.add(route.driver_id);
                    }
                });

                // Filter to only unassigned drivers
                const allDrivers = response.data.data || response.data.drivers || [];
                const unassigned = allDrivers.filter(d => !assignedDriverIds.has(d.id));
                setAvailableDrivers(unassigned);
            }
        } catch (error) {
            console.error('Error loading drivers:', error);
            setError('Failed to load available drivers');
        } finally {
            setLoadingDrivers(false);
        }
    };

    const handleCreateRoute = async () => {
        if (!newRouteName.trim()) {
            setError('Route name is required');
            return;
        }
        setCreatingRoute(true);
        try {
            const token = localStorage.getItem('token');
            await http.post('/admin/driver-management/routes', { name: newRouteName.trim() }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessage(`✅ Route "${newRouteName.trim()}" created successfully!`);
            setNewRouteName('');
            setShowCreateModal(false);
            loadRoutes();
        } catch (error) {
            console.error('Error creating route:', error);
            setError(error.response?.data?.message || 'Failed to create route');
        } finally {
            setCreatingRoute(false);
        }
    };

    const openAssignModal = (route) => {
        setSelectedRoute(route);
        setSelectedDriverId('');
        setShowAssignModal(true);
        loadAvailableDrivers();
    };

    const assignDriver = async () => {
        if (!selectedDriverId) {
            setError('Please select a driver to assign');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await http.post(
                `/admin/logistics/routes/${selectedRoute.id}/assign-driver`,
                { driverId: selectedDriverId },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const driver = availableDrivers.find(d => d.id === selectedDriverId);
            const driverName = driver?.full_name || driver?.email || 'Driver';
            setMessage(`✅ ${driverName} assigned to route "${selectedRoute.name}"`);
            setShowAssignModal(false);
            loadRoutes();
        } catch (error) {
            console.error('Error assigning driver:', error);
            setError(error.response?.data?.message || 'Failed to assign driver');
        }
    };

    const unassignDriver = async (routeId, routeName) => {
        const confirmed = window.confirm(
            `Are you sure you want to unassign the driver from route "${routeName}"?`
        );
        if (!confirmed) return;

        try {
            const token = localStorage.getItem('token');
            await http.post(
                `/admin/logistics/routes/${routeId}/unassign-driver`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessage(`✅ Driver unassigned from route "${routeName}"`);
            loadRoutes();
        } catch (error) {
            console.error('Error unassigning driver:', error);
            setError(error.response?.data?.message || 'Failed to unassign driver');
        }
    };

    const deleteRoute = async (routeId, routeName) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete the route "${routeName}"?\n\nThis route can only be deleted if it's not assigned to any customers.`
        );

        if (!confirmed) return;

        try {
            const token = localStorage.getItem('token');
            const response = await http.delete(`/admin/driver-management/routes/${routeId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setMessage(`✅ Route "${routeName}" deleted successfully!`);
                loadRoutes();
            }
        } catch (error) {
            console.error('Error deleting route:', error);
            const errorMsg = error.response?.data?.message || 'Failed to delete route';
            setError(errorMsg);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-white">Driver Management</h2>
                    <p className="text-gray-400 mt-1">Manage driver assignments for routes</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => { setNewRouteName(''); setShowCreateModal(true); }}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded text-sm font-medium"
                    >
                        + Add Route
                    </button>
                    <div className="bg-gray-700 rounded-lg px-4 py-2 border border-gray-600">
                        <p className="text-xs text-gray-400">
                            💡 Create new driver accounts in the <span className="text-orange-400 font-semibold">User Management</span> tab
                        </p>
                    </div>
                </div>
            </div>

            {/* Routes Table */}
            {loading ? (
                <div className="text-center py-8 text-gray-400">Loading routes...</div>
            ) : (
                <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-orange-500">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-black">
                                <tr>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">
                                        Route Name
                                    </th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">
                                        Current Driver
                                    </th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-gray-900 divide-y divide-gray-800">
                                {routes.length === 0 ? (
                                    <tr>
                                        <td colSpan="3" className="px-3 sm:px-6 py-8 text-center text-gray-400">
                                            No routes found
                                        </td>
                                    </tr>
                                ) : (
                                    routes.map((route) => (
                                        <tr key={route.id} className="hover:bg-gray-800">
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <div className="text-xs sm:text-sm font-medium text-white">{route.name}</div>
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                {route.driver_username ? (
                                                    <div className="text-xs sm:text-sm text-white bg-green-900 px-2 sm:px-3 py-1 sm:py-2 rounded inline-block font-medium">
                                                        {route.driver_username}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs sm:text-sm text-gray-500 italic">No driver</span>
                                                )}
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => openAssignModal(route)}
                                                        className="bg-orange-500 hover:bg-orange-600 text-white px-2 sm:px-4 py-1 sm:py-2 rounded text-xs sm:text-sm"
                                                    >
                                                        <span className="hidden sm:inline">{route.driver_username ? 'Reassign Driver' : 'Assign Driver'}</span>
                                                        <span className="inline sm:hidden">{route.driver_username ? 'Reassign' : 'Assign'}</span>
                                                    </button>
                                                    {route.driver_username && (
                                                        <button
                                                            onClick={() => unassignDriver(route.id, route.name)}
                                                            className="bg-yellow-600 hover:bg-yellow-700 text-white px-2 sm:px-4 py-1 sm:py-2 rounded text-xs sm:text-sm"
                                                        >
                                                            <span className="hidden sm:inline">Unassign Driver</span>
                                                            <span className="inline sm:hidden">Unassign</span>
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => deleteRoute(route.id, route.name)}
                                                        className="bg-red-500 hover:bg-red-600 text-white px-2 sm:px-4 py-1 sm:py-2 rounded text-xs sm:text-sm"
                                                    >
                                                        <span className="hidden sm:inline">Delete Route</span>
                                                        <span className="inline sm:hidden">Delete</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Create Route Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-lg max-w-md w-full p-6 border-2 border-orange-500">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-white">Create Route</h2>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="text-gray-400 hover:text-white text-2xl"
                            >
                                ×
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Route Name
                                </label>
                                <input
                                    type="text"
                                    value={newRouteName}
                                    onChange={(e) => setNewRouteName(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreateRoute()}
                                    placeholder="e.g. Route A"
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:border-orange-500"
                                />
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={handleCreateRoute}
                                    disabled={creatingRoute || !newRouteName.trim()}
                                    className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md"
                                >
                                    {creatingRoute ? 'Creating...' : 'Create Route'}
                                </button>
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    className="flex-1 border border-gray-600 hover:bg-gray-800 text-gray-300 px-4 py-2 rounded-md"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Driver Modal */}
            {showAssignModal && selectedRoute && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-lg max-w-md w-full p-6 border-2 border-orange-500">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-white">Assign Driver</h2>
                            <button
                                onClick={() => setShowAssignModal(false)}
                                className="text-gray-400 hover:text-white text-2xl"
                            >
                                ×
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <p className="text-gray-300 mb-4">
                                    Assigning driver to route: <span className="font-bold text-orange-500">{selectedRoute.name}</span>
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Select Driver
                                </label>
                                {loadingDrivers ? (
                                    <div className="text-gray-400 text-sm py-2">Loading drivers...</div>
                                ) : availableDrivers.length === 0 ? (
                                    <div className="bg-gray-800 border border-gray-700 rounded-md p-3">
                                        <p className="text-gray-400 text-sm">No unassigned drivers available.</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Create a new driver account in the <span className="text-orange-400 font-semibold">User Management</span> tab first.
                                        </p>
                                    </div>
                                ) : (
                                    <select
                                        value={selectedDriverId}
                                        onChange={(e) => setSelectedDriverId(e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:border-orange-500"
                                    >
                                        <option value="">Choose a driver...</option>
                                        {availableDrivers.map(driver => (
                                            <option key={driver.id} value={driver.id}>
                                                {driver.full_name || driver.email}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={assignDriver}
                                    disabled={!selectedDriverId}
                                    className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md"
                                >
                                    Assign Driver
                                </button>
                                <button
                                    onClick={() => setShowAssignModal(false)}
                                    className="flex-1 border border-gray-600 hover:bg-gray-800 text-gray-300 px-4 py-2 rounded-md"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DriverManagementTab;
